/**
 * A Hide and Seek round's Max Camera Distance limit (SWTOR Preferences →
 * Controls). Each player's client reads the value the game uses from SWTOR's
 * settings and reports it. A player over the limit holds up the round's
 * start; one who raises it over the limit mid-round has
 * CAMERA_DISTANCE_GRACE_SECONDS to lower it again or is disqualified. The
 * admin can switch the check off for a player who can't change it.
 */
import { isHideSeekSettings, isHnstSettings } from './round';
/**
 * The in-game slider's range, as SWTOR saves it: whole numbers from 3 (0%)
 * to 25 (100%). The game shows each step as a percentage rounded up, so 5
 * shows as 10% and 19 as 73% (see cameraDistancePercent). A player can type
 * a bigger number into the file by hand and the game uses it, so a reported
 * value can be far beyond 25; a round's limit is always on the slider.
 */
export const HNS_MAX_CAMERA_DISTANCE_MIN = 3;
export const HNS_MAX_CAMERA_DISTANCE_MAX = 25;
/**
 * The percentage SWTOR's slider shows for a saved value: (value − 3) ÷ 22,
 * rounded up. Over 100 for a value typed into the file beyond the slider.
 */
export function cameraDistancePercent(value) {
    const range = HNS_MAX_CAMERA_DISTANCE_MAX - HNS_MAX_CAMERA_DISTANCE_MIN;
    // Rounded first so 50% (14) isn't pushed to 51% by floating point.
    return Math.ceil(Math.round(((value - HNS_MAX_CAMERA_DISTANCE_MIN) * 100 * 1e6) / range) / 1e6);
}
/** A value as players see it on the slider: `50%`, or `1000 (beyond the slider)`. */
export function formatCameraDistance(value) {
    return value > HNS_MAX_CAMERA_DISTANCE_MAX ? `${value} (beyond the slider)` : `${cameraDistancePercent(value)}%`;
}
/** How long a player who raises their camera distance mid-round has to lower it again. */
export const CAMERA_DISTANCE_GRACE_SECONDS = 15;
/** The round's limit, or null when it has none. */
export function roundMaxCameraDistance(round) {
    return isHideSeekSettings(round.settings) ? (round.settings.maxCameraDistance ?? null) : null;
}
export function cameraCheckStatus(round, player) {
    const limit = roundMaxCameraDistance(round);
    if (limit === null)
        return 'none';
    if (round.cameraCheckOff?.includes(player.playerId))
        return 'off';
    const value = player.cameraDistance?.value ?? null;
    if (value === null)
        return 'unknown';
    return value > limit ? 'over' : 'ok';
}
/** Whether a status holds up the round's start. */
export function cameraCheckBlocksStart(status) {
    return status === 'over' || status === 'unknown';
}
/**
 * Who the round's camera check applies to: its players, or in a Hide and
 * Seek Teams round still in setup (players are fixed when it starts) the
 * teams' enabled, connected members, as the round will take them.
 */
export function cameraCheckPlayerIds(game, round) {
    if (!isHnstSettings(round.settings) || round.status !== 'setup')
        return round.players.map((p) => p.playerId);
    const players = new Map(game.players.map((p) => [p.playerId, p]));
    return (game.hnstTeams ?? [])
        .flatMap((team) => team.memberIds)
        .filter((playerId) => {
        const player = players.get(playerId);
        return !!player && player.enabled !== false && player.status === 'connected';
    });
}
